import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "d-flex gap-2" }
const _hoisted_2 = { class: "filters-container mb-4" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 1,
  class: "text-grey"
}
const _hoisted_5 = { class: "skills-container" }
const _hoisted_6 = {
  key: 0,
  class: "text-grey"
}
const _hoisted_7 = ["href"]
const _hoisted_8 = {
  key: 0,
  class: "text-grey"
}
const _hoisted_9 = { class: "mb-3" }

import { ref, onMounted, computed } from 'vue'
import { api } from '@/composables/api'
import type { Volunteer } from '@/types/Volunteer'


export default /*@__PURE__*/_defineComponent({
  __name: 'VolunteersTable',
  setup(__props) {

const volunteers = ref<Volunteer[]>([])
const loading = ref(false)
const searchQuery = ref('')
const interestFilter = ref('')
const showViewModal = ref(false)
const showEmailModal = ref(false)
const showEditModal = ref(false)
const viewingVolunteer = ref<Volunteer | null>(null)
const editingVolunteer = ref<Volunteer | null>(null)
const downloadingCSV = ref(false)
const saving = ref(false)

const headers = [
  { title: 'Name', key: 'name', sortable: true },
  { title: 'Email', key: 'email', sortable: true },
  { title: 'Phone', key: 'phone', sortable: true },
  { title: 'Interests', key: 'skills', sortable: false },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const interestOptions = [
  { title: 'Computer Refurbishment', value: 'computer_refurbishment' },
  { title: 'Distribution/Delivery', value: 'distribution_delivery' },
  { title: 'STEM Mentoring', value: 'stem_mentoring' },
  { title: 'STEM Workshops/Career Days', value: 'stem_workshops' }
]

// Helper functions for interests
const getInterestLabel = (interest: string) => {
  const labels: Record<string, string> = {
    'computer_refurbishment': 'Computer Refurbishment',
    'distribution_delivery': 'Distribution/Delivery',
    'stem_mentoring': 'STEM Mentoring',
    'stem_workshops': 'STEM Workshops/Career Days'
  }
  return labels[interest] || interest
}

const getInterestShortLabel = (interest: string) => {
  const labels: Record<string, string> = {
    'computer_refurbishment': 'Refurbishment',
    'distribution_delivery': 'Delivery',
    'stem_mentoring': 'Mentoring',
    'stem_workshops': 'Workshops'
  }
  return labels[interest] || interest
}

const getInterestIcon = (interest: string) => {
  const icons: Record<string, string> = {
    'computer_refurbishment': 'mdi-tools',
    'distribution_delivery': 'mdi-truck-delivery',
    'stem_mentoring': 'mdi-account-tie',
    'stem_workshops': 'mdi-school'
  }
  return icons[interest] || 'mdi-help-circle'
}

// Filtered volunteers based on search query and interest filter
const filteredVolunteers = computed(() => {
  let filtered = volunteers.value

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(volunteer =>
      volunteer.name.toLowerCase().includes(query) ||
      volunteer.email.toLowerCase().includes(query) ||
      (volunteer.phone && volunteer.phone.toLowerCase().includes(query))
    )
  }

  // Apply interest filter
  if (interestFilter.value) {
    filtered = filtered.filter(volunteer =>
      volunteer.interested_in?.includes(interestFilter.value)
    )
  }

  return filtered
})

// Computed property for emails list
const emailsList = computed(() => {
  return filteredVolunteers.value.map(v => v.email).join(', ')
})

const loadVolunteers = async () => {
  loading.value = true
  try {
    const response = await api.get('/volunteers')
    volunteers.value = response.data || response || []
  } catch (error) {
    console.error('Error loading volunteers:', error)
    alert('Failed to load volunteers')
    volunteers.value = []
  } finally {
    loading.value = false
  }
}

const openViewModal = (volunteer: any) => {
  viewingVolunteer.value = volunteer
  showViewModal.value = true
}

const closeViewModal = () => {
  showViewModal.value = false
  viewingVolunteer.value = null
}

const openEditModal = (volunteer: any) => {
  // Create a deep copy to avoid mutating the original
  editingVolunteer.value = JSON.parse(JSON.stringify(volunteer))
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  editingVolunteer.value = null
}

const saveVolunteer = async () => {
  if (!editingVolunteer.value) return

  saving.value = true
  try {
    await api.put(`/volunteers/${editingVolunteer.value.id}`, {
      name: editingVolunteer.value.name,
      email: editingVolunteer.value.email,
      phone: editingVolunteer.value.phone,
      interested_in: editingVolunteer.value.interested_in,
      status: editingVolunteer.value.status,
      comments: editingVolunteer.value.comments
    })

    await loadVolunteers()
    closeEditModal()
    alert('Volunteer updated successfully!')
  } catch (error) {
    console.error('Error updating volunteer:', error)
    alert('Failed to update volunteer')
  } finally {
    saving.value = false
  }
}

const deactivateVolunteer = async (volunteer: any) => {
  if (!confirm(`Are you sure you want to deactivate ${volunteer.name}?`)) {
    return
  }

  try {
    await api.post(`/volunteers/${volunteer.id}/deactivate`)
    await loadVolunteers()
  } catch (error) {
    console.error('Error deactivating volunteer:', error)
    alert('Failed to deactivate volunteer')
  }
}

const activateVolunteer = async (volunteer: any) => {
  try {
    await api.post(`/volunteers/${volunteer.id}/activate`)
    await loadVolunteers()
  } catch (error) {
    console.error('Error activating volunteer:', error)
    alert('Failed to activate volunteer')
  }
}

const deleteVolunteer = async (volunteer: any) => {
  if (!confirm(`Are you sure you want to delete ${volunteer.name}? This action cannot be undone.`)) {
    return
  }

  try {
    await api.delete(`/volunteers/${volunteer.id}`)
    await loadVolunteers()
  } catch (error) {
    console.error('Error deleting volunteer:', error)
    alert('Failed to delete volunteer')
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const copyEmails = async () => {
  try {
    await navigator.clipboard.writeText(emailsList.value)
    alert('Email addresses copied to clipboard!')
  } catch (error) {
    console.error('Error copying to clipboard:', error)
    alert('Failed to copy emails. Please select and copy manually.')
  }
}

const downloadCSV = async () => {
  downloadingCSV.value = true
  try {
    // Generate CSV content using filtered volunteers
    const headers = ['Name', 'Email', 'Phone', 'Interests', 'Status', 'Comments', 'Signup Date']
    const csvRows = [headers.join(',')]

    filteredVolunteers.value.forEach(volunteer => {
      const interests = volunteer.interested_in?.map(i => getInterestLabel(i)).join('; ') || ''
      const row = [
        `"${volunteer.name}"`,
        `"${volunteer.email}"`,
        `"${volunteer.phone || ''}"`,
        `"${interests}"`,
        volunteer.status,
        `"${(volunteer.comments || '').replace(/"/g, '""')}"`,
        formatDate(volunteer.created_at)
      ]
      csvRows.push(row.join(','))
    })

    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `volunteers_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Error downloading CSV:', error)
    alert('Failed to download CSV')
  } finally {
    downloadingCSV.value = false
  }
}

onMounted(() => {
  loadVolunteers()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_card, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center" }, {
        default: _withCtx(() => [
          _cache[18] || (_cache[18] = _createElementVNode("span", null, "Volunteers Management", -1)),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_v_btn, {
              color: "secondary",
              variant: "outlined",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (showEmailModal.value = true))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("mdi-email-multiple", -1)
                  ]))]),
                  _: 1
                }),
                _cache[15] || (_cache[15] = _createTextVNode(" Copy Emails ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_btn, {
              color: "primary",
              onClick: downloadCSV,
              loading: downloadingCSV.value
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                    _createTextVNode("mdi-download", -1)
                  ]))]),
                  _: 1
                }),
                _cache[17] || (_cache[17] = _createTextVNode(" Download CSV ", -1))
              ]),
              _: 1
            }, 8, ["loading"])
          ])
        ]),
        _: 1
      }),
      _createVNode(_component_v_card_text, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_v_text_field, {
              modelValue: searchQuery.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
              label: "Search volunteers...",
              "prepend-inner-icon": "mdi-magnify",
              variant: "outlined",
              density: "compact",
              clearable: "",
              "hide-details": "",
              class: "search-field"
            }, null, 8, ["modelValue"]),
            _createVNode(_component_v_select, {
              modelValue: interestFilter.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((interestFilter).value = $event)),
              label: "Filter by interest",
              items: interestOptions,
              variant: "outlined",
              density: "compact",
              clearable: "",
              "hide-details": "",
              class: "interest-filter"
            }, null, 8, ["modelValue"])
          ]),
          _createVNode(_component_v_data_table, {
            headers: headers,
            items: filteredVolunteers.value,
            loading: loading.value,
            "items-per-page": 10,
            class: "elevation-1",
            "onClick:row": openViewModal,
            hover: ""
          }, {
            "item.status": _withCtx(({ item }) => [
              _createVNode(_component_v_chip, {
                color: (item as any).status === 'active' ? 'success' : 'grey',
                size: "small"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString((item as any).status), 1)
                ]),
                _: 2
              }, 1032, ["color"])
            ]),
            "item.phone": _withCtx(({ item }) => [
              ((item as any).phone)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString((item as any).phone), 1))
                : (_openBlock(), _createElementBlock("span", _hoisted_4, "-"))
            ]),
            "item.skills": _withCtx(({ item }) => [
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((item as any).interested_in, (interest) => {
                  return (_openBlock(), _createBlock(_component_v_chip, {
                    key: interest,
                    size: "x-small",
                    color: "primary",
                    class: "mr-1 mb-1",
                    title: getInterestLabel(interest)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(getInterestShortLabel(interest)), 1)
                    ]),
                    _: 2
                  }, 1032, ["title"]))
                }), 128)),
                (!(item as any).interested_in?.length)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_6, "-"))
                  : _createCommentVNode("", true)
              ])
            ]),
            "item.actions": _withCtx(({ item }) => [
              _createVNode(_component_v_btn, {
                icon: "mdi-pencil",
                size: "small",
                variant: "text",
                onClick: _withModifiers(($event: any) => (openEditModal(item)), ["stop"]),
                title: "Edit"
              }, null, 8, ["onClick"]),
              _createVNode(_component_v_btn, {
                icon: "mdi-delete",
                size: "small",
                variant: "text",
                color: "error",
                onClick: _withModifiers(($event: any) => (deleteVolunteer(item)), ["stop"]),
                title: "Delete"
              }, null, 8, ["onClick"])
            ]),
            _: 1
          }, 8, ["items", "loading"])
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showViewModal.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showViewModal).value = $event)),
        "max-width": "600px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                  _createElementVNode("span", { class: "text-h5" }, "Volunteer Details", -1)
                ]))]),
                _: 1
              }),
              (viewingVolunteer.value)
                ? (_openBlock(), _createBlock(_component_v_card_text, { key: 0 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item_title, { class: "font-weight-bold" }, {
                                default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                  _createTextVNode("Name", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item_subtitle, null, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(viewingVolunteer.value.name), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_list_item, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item_title, { class: "font-weight-bold" }, {
                                default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                  _createTextVNode("Email", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item_subtitle, null, {
                                default: _withCtx(() => [
                                  _createElementVNode("a", {
                                    href: `mailto:${viewingVolunteer.value.email}`
                                  }, _toDisplayString(viewingVolunteer.value.email), 9, _hoisted_7)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_list_item, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item_title, { class: "font-weight-bold" }, {
                                default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                                  _createTextVNode("Phone", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item_subtitle, null, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(viewingVolunteer.value.phone || 'Not provided'), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_list_item, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item_title, { class: "font-weight-bold" }, {
                                default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                                  _createTextVNode("Status", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item_subtitle, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_chip, {
                                    color: viewingVolunteer.value.status === 'active' ? 'success' : 'grey',
                                    size: "small"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(viewingVolunteer.value.status), 1)
                                    ]),
                                    _: 1
                                  }, 8, ["color"])
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_divider, { class: "my-3" }),
                          _createVNode(_component_v_list_item, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item_title, { class: "font-weight-bold mb-2" }, {
                                default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                                  _createTextVNode("How They Want to Help", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item_subtitle, null, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(viewingVolunteer.value.interested_in, (interest) => {
                                    return (_openBlock(), _createBlock(_component_v_chip, {
                                      key: interest,
                                      size: "small",
                                      color: "primary",
                                      class: "mr-2 mb-2"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          start: "",
                                          size: "small"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(getInterestIcon(interest)), 1)
                                          ]),
                                          _: 2
                                        }, 1024),
                                        _createTextVNode(" " + _toDisplayString(getInterestLabel(interest)), 1)
                                      ]),
                                      _: 2
                                    }, 1024))
                                  }), 128)),
                                  (!viewingVolunteer.value.interested_in?.length)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, " No interests specified "))
                                    : _createCommentVNode("", true)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_divider, { class: "my-3" }),
                          (viewingVolunteer.value.comments)
                            ? (_openBlock(), _createBlock(_component_v_list_item, { key: 0 }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, { class: "font-weight-bold" }, {
                                    default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                      _createTextVNode("Comments", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, { class: "text-wrap" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(viewingVolunteer.value.comments), 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          _createVNode(_component_v_list_item, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item_title, { class: "font-weight-bold" }, {
                                default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                                  _createTextVNode("Signup Date", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item_subtitle, null, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(formatDate(viewingVolunteer.value.created_at)), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: closeViewModal
                  }, {
                    default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                      _createTextVNode("Close", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showEmailModal.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showEmailModal).value = $event)),
        "max-width": "600px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                  _createElementVNode("span", { class: "text-h5" }, "Volunteer Emails", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createElementVNode("p", _hoisted_9, _toDisplayString(filteredVolunteers.value.length) + " volunteer" + _toDisplayString(filteredVolunteers.value.length !== 1 ? 's' : '') + " " + _toDisplayString(interestFilter.value ? `interested in ${getInterestLabel(interestFilter.value)}` : ''), 1),
                  _createVNode(_component_v_textarea, {
                    modelValue: emailsList.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((emailsList).value = $event)),
                    rows: "10",
                    readonly: "",
                    variant: "outlined",
                    label: "Email addresses (comma-separated)",
                    "auto-grow": ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (showEmailModal.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                      _createTextVNode("Close", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: copyEmails
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { start: "" }, {
                        default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                          _createTextVNode("mdi-content-copy", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[31] || (_cache[31] = _createTextVNode(" Copy to Clipboard ", -1))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showEditModal.value,
        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((showEditModal).value = $event)),
        "max-width": "700px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                  _createElementVNode("span", { class: "text-h5" }, "Edit Volunteer", -1)
                ]))]),
                _: 1
              }),
              (editingVolunteer.value)
                ? (_openBlock(), _createBlock(_component_v_card_text, { key: 0 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_form, { ref: "editForm" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: editingVolunteer.value.name,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editingVolunteer.value.name) = $event)),
                            label: "Name",
                            variant: "outlined",
                            density: "compact",
                            required: "",
                            class: "mb-3"
                          }, null, 8, ["modelValue"]),
                          _createVNode(_component_v_text_field, {
                            modelValue: editingVolunteer.value.email,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((editingVolunteer.value.email) = $event)),
                            label: "Email",
                            type: "email",
                            variant: "outlined",
                            density: "compact",
                            required: "",
                            class: "mb-3"
                          }, null, 8, ["modelValue"]),
                          _createVNode(_component_v_text_field, {
                            modelValue: editingVolunteer.value.phone,
                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((editingVolunteer.value.phone) = $event)),
                            label: "Phone",
                            variant: "outlined",
                            density: "compact",
                            class: "mb-3"
                          }, null, 8, ["modelValue"]),
                          _createVNode(_component_v_select, {
                            modelValue: editingVolunteer.value.interested_in,
                            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((editingVolunteer.value.interested_in) = $event)),
                            items: interestOptions,
                            label: "Interests",
                            variant: "outlined",
                            density: "compact",
                            multiple: "",
                            chips: "",
                            "closable-chips": "",
                            class: "mb-3"
                          }, null, 8, ["modelValue"]),
                          _createVNode(_component_v_select, {
                            modelValue: editingVolunteer.value.status,
                            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((editingVolunteer.value.status) = $event)),
                            items: ['active', 'inactive'],
                            label: "Status",
                            variant: "outlined",
                            density: "compact",
                            class: "mb-3"
                          }, null, 8, ["modelValue"]),
                          _createVNode(_component_v_textarea, {
                            modelValue: editingVolunteer.value.comments,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((editingVolunteer.value.comments) = $event)),
                            label: "Comments",
                            variant: "outlined",
                            rows: "3",
                            "auto-grow": ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }, 512)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: closeEditModal
                  }, {
                    default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: saveVolunteer,
                    loading: saving.value
                  }, {
                    default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                      _createTextVNode(" Save Changes ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})